import ViewViewModel from '@/ViewModels/ViewViewModel';
import ComponentManager from '@/Management/ComponentManager';
import WindowViewModelT from '@/ViewModels/Controls/WindowViewModel';
import GridViewModelT from '@/ViewModels/Controls/GridViewModel';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import { GridColumnDefinition } from '#/DefaultViewExtensionInterfaces';
import SimpleLinkBuilder from '@/Builder/SimpleLinkBuilder';
import SimpleLabelBuilder from '@/Builder/SimpleLabelBuilder';
import ResourceManager from '@/Management/ResourceManager';
import * as DownloadHelper from '@/Utility/DownloadHelper';
import * as Interfaces from '#/CoreFrameInterfaces';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import { IOperationResultDTO } from "coreframetypings/dto";

interface ILicenseDTO {
    library: string;
    link: string;
    version: string;
    source?: string;
    type?: string;
    info?: string;
}

export default class ThirdPartyComponentsViewViewModel extends ViewViewModel {

    private WindowViewModel = ComponentManager.getViewModel<typeof WindowViewModelT>('WindowViewModel');
    private GridViewModel = ComponentManager.getViewModel<typeof GridViewModelT>('GridViewModel');
    private LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');

    /**
    * Das Fenster
    */
    public window: WindowViewModelT;

    private onOpen: Function;
    /**
    * Handler für das Schließen des Fensters
    */
    private onClose: Function;

    /**
    * Das Grid mit den sichtbaren Spalten
    */
    public thirdPartyGrid: GridViewModelT;

    constructor(params: Interfaces.IThirdPartyComponentsViewViewModelConstructorParameter) {
        super(params);

        this.onOpen = this.handlerOrDefault(params.onOpen);
        this.onClose = this.handlerOrDefault(params.onClose);
        this.createWindowViewModel();
        this.createGridViewModel();
        this.getThirdPartyGridData();
    }

    private createWindowViewModel(): void {
        this.window = new this.WindowViewModel({
            isOpen: true,
            allowClose: true,
            parentLocator: this.locator,
            locator: 'window',
            headline: new this.LabelViewModel({
                ergName: 'THIRD_PARTY_COMPONENTS'
            }),
            isModal: true,
            width: '75%',
            height: '75%',
            onOpen: this.onOpen,
            onClose: this.onClose
        });
    }

    private createGridViewModel(): void {
        const columnDefinition = this.createColumnDefinition();

        this.thirdPartyGrid = new this.GridViewModel({
            columnDefinition: columnDefinition
        });
    }

    private createColumnDefinition(): GridColumnDefinition[] {
        return [
            {
                name: 'library',
                ergName: 'THIRD_PARTY_COMPONENTS.LIBRARY',
                field: 'library',
                isVisible: true,
                width: '300px'
            },
            GridViewModelT.createColumnDefinition(true, 'THIRD_PARTY_COMPONENTS.VERSION', 'version'),
            {
                name: 'link',
                ergName: 'THIRD_PARTY_COMPONENTS.LICENSE',
                field: 'link',
                isVisible: true,
                uiGenerator: (link: string, license: ILicenseDTO) => this.getLinkColumnHTMLElement(link, license),
                width: '450px'
            },
            GridViewModelT.createColumnDefinition(true, 'NOTICE_TITLE', 'info'),
        ];
    }

    private getLinkColumnHTMLElement(link: string, license: ILicenseDTO): HTMLElement {
        if (!link) {
            license.info = ResourceManager.getInstance().get('THIRD_PARTY_COMPONENTS.NO_LICENSE_LINK');
            const caption = license.type || '-';

            if (this.isExternalLink(caption)) {
                return new SimpleLinkBuilder()
                    .withCaption(caption)
                    .withOnClick(() => window.open(caption, '_blank'))
                    .buildAndGetElement();
            }

            return new SimpleLabelBuilder()
                .withCaption(caption)
                .buildAndGetElement()
        }

        if (this.isExternalLink(link)) {
            license.info = ResourceManager.getInstance().get('THIRD_PARTY_COMPONENTS.NO_LICENSE_FILE');
            return new SimpleLinkBuilder()
                .withCaption(link)
                .withOnClick(() => window.open(link, '_blank'))
                .buildAndGetElement();
        }

        return new SimpleLinkBuilder()
            .withCaption(license.type)
            .withOnClick(() => this.downloadLicense(link))
            .buildAndGetElement();
    }

    private isExternalLink(url: string): boolean {
        return url.startsWith('http');
    }

    private getThirdPartyGridData(): void {
        this.thirdPartyGrid.isBusy(true)

        RADEnvironmentProxy.getInstance().callStaticOperationWithoutTransaction({
            pdTypeName: 'RAD.RADFoundation.ProblemDomain.Meta.PDApplicationMeta',
            operationName: 'GetLicenseAgreements',
            operationParameters: [],
            contextParameters: {}
        }).then(this.handleThirdPartyGridData.bind(this))
            .always(() => {
                if (!this.isDisposed)
                    this.thirdPartyGrid.isBusy(false);
            })
    }

    private handleThirdPartyGridData(result: IOperationResultDTO): void {
        if (this.isDisposed || !this.validateStatus(result))
            return;

        const resultDTO: { Notes: string[], Licenses: [] } = JSON.parse(result.returnValue);

        if (!resultDTO || !resultDTO.Licenses)
            return;

        const gridData: ILicenseDTO[] = resultDTO.Licenses.map((license: any): ILicenseDTO => ({
            library: license.Libary,
            link: license.Link,
            source: license.Source,
            type: license.Type,
            version: license.Version
        }));

        this.thirdPartyGrid.data(gridData);
    }

    private downloadLicense(link: string): void {
        this.thirdPartyGrid.isBusy(true)

        RADEnvironmentProxy.getInstance().callStaticOperationWithoutTransaction({
            pdTypeName: 'RAD.RADFoundation.ProblemDomain.Meta.PDApplicationMeta',
            operationName: 'CreateLicenseDownloadLink',
            operationParameters: [link],
            contextParameters: {}
        }).then((operationResult) => {
            if (this.isDisposed || !this.validateStatus(operationResult))
                return;

            const url = operationResult.returnValue;
            DownloadHelper.openDownloadDialogFromApplicationAsync(url);
        }
        ).always(() => {
            if (!this.isDisposed)
                this.thirdPartyGrid.isBusy(false);
        });
    }
}

ComponentManager.setViewModel('ThirdPartyComponentsViewViewModel', ThirdPartyComponentsViewViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');